import type {
  IAddVerifiedDomainParams,
  IAdoptOrganizationMcpConnectionParams,
  ICallAssistantSettingsResponse,
  IChatSettings,
  ICreateOrganizationBody,
  ICreateOrganizationRoleParams,
  ICreateOrgModelParams,
  ICreateSyncatorApiKeyParams,
  IDeleteOrganizationMcpConnectionParams,
  IDeleteOrganizationParams,
  IDeleteOrganizationRoleParams,
  IDeleteOrgModelParams,
  IEditOrganizationParams,
  IEmbeddingModelOption,
  IGetOrgModelUsageParams,
  IModel,
  IOrganization,
  IOrganizationAssistant,
  IOrganizationBannedMember,
  IOrganizationDataRetention,
  IOrganizationInvite,
  IOrganizationMcpApprovalTarget,
  IOrganizationMcpConnection,
  IOrganizationMcpIntegration,
  IOrganizationMember,
  IOrganizationMemberBanParams,
  IOrganizationMemoryExclusionsView,
  IOrganizationModel,
  IOrganizationModelTokenLimits,
  IOrganizationOwnedModel,
  IOrganizationRole,
  IOrganizationSiemConfig,
  IOrganizationUsageLimits,
  IOrganizationUsageLimitsOverview,
  IOrgModelUsage,
  IProbeModelDefaults,
  IRemoveOrganizationMemberParams,
  IRemoveOrganizationOwnerParams,
  IRemoveVerifiedDomainParams,
  IResendOrganizationInviteParams,
  IRevokeOrganizationInviteParams,
  IRevokeSyncatorApiKeyParams,
  ISetModelApprovalParams,
  ISetOrgModelOrgVisibilityParams,
  ISetOrgModelOrgWideVisibilityParams,
  ISyncatorApiKey,
  ISyncatorApiKeyCreateResponse,
  ITestOrganizationSiemConfigParams,
  ITransferOrganizationOwnershipParams,
  IUpdateCallAssistantSettingsParams,
  IUpdateOrganizationChatSettingsParams,
  IUpdateOrganizationDataRetentionParams,
  IUpdateOrganizationIntegrationConfigParams,
  IUpdateOrganizationMemoryExclusionsParams,
  IUpdateOrganizationModelTokenLimitsParams,
  IUpdateOrganizationRoleParams,
  IUpdateOrganizationSamlParams,
  IUpdateOrganizationSearchSettingsParams,
  IUpdateOrganizationSiemConfigParams,
  IUpdateOrganizationUsageLimitsParams,
  IUpdateOrgModelParams,
  IUpdateProbeModelSettingsParams,
  IVerifiedDomain,
  IVerifyVerifiedDomainParams,
  TSiemConnectionTestResult,
} from "../types";
import type { ApiClient } from "./client";

export class OrganizationApi {
  constructor(private apiClient: ApiClient) {}

  async getOrganizations(): Promise<IOrganization[]> {
    const response =
      await this.apiClient.get<IOrganization[]>("/organizations");
    return response.data;
  }

  async create(data: ICreateOrganizationBody): Promise<IOrganization> {
    const response = await this.apiClient.post<IOrganization>(
      "/organizations",
      data,
    );
    return response.data;
  }

  async sendVerificationCode(email: string): Promise<void> {
    const response = await this.apiClient.post<void>(
      "/organizations/send-verification-code",
      { email },
    );
    return response.data;
  }

  async edit({ uuid, data }: IEditOrganizationParams): Promise<IOrganization> {
    const response = await this.apiClient.patch<IOrganization>(
      `/organizations/${uuid}`,
      data,
    );
    return response.data;
  }

  async delete({
    uuid,
    verificationCode,
  }: IDeleteOrganizationParams): Promise<void> {
    await this.apiClient.delete(`/organizations/${uuid}`, {
      data: { verificationCode },
    });
  }

  async leave(uuid: string): Promise<void> {
    await this.apiClient.post(`/organizations/${uuid}/leave`);
  }

  /**
   * NOTE: NSAI-6227 — owner-only removal of another member from the whole
   * organization, and so from every project in it. The self-service
   * counterpart is leave() above; a non-owner gets a 404 from the server.
   */
  async removeMember({
    uuid,
    data,
  }: IRemoveOrganizationMemberParams): Promise<void> {
    await this.apiClient.post(`/organizations/${uuid}/remove-member`, data);
  }

  /**
   * NOTE: NSAI-6515 — owner-only. Removes the member exactly as removeMember
   * does, ends their sessions and this organization's API keys, and keeps
   * every way back in (SSO, invitations, a project admin's add) closed until
   * unbanMember. A non-owner gets a 404 from the server.
   */
  async banMember({ uuid, data }: IOrganizationMemberBanParams): Promise<void> {
    await this.apiClient.post(`/organizations/${uuid}/ban-member`, data);
  }

  /**
   * NOTE: NSAI-6515 — lifts the ban and re-adds nobody: the person comes back
   * through the normal paths (SSO, an invitation), which no longer refuse them.
   */
  async unbanMember({
    uuid,
    data,
  }: IOrganizationMemberBanParams): Promise<void> {
    await this.apiClient.post(`/organizations/${uuid}/unban-member`, data);
  }

  async getBannedMembers(uuid: string): Promise<IOrganizationBannedMember[]> {
    const response = await this.apiClient.get<IOrganizationBannedMember[]>(
      `/organizations/${uuid}/banned-members`,
    );
    return response.data;
  }

  async getMembers(uuid: string): Promise<IOrganizationMember[]> {
    const response = await this.apiClient.get<IOrganizationMember[]>(
      `/organizations/${uuid}/members`,
    );
    return response.data;
  }

  async getModels(uuid: string): Promise<IOrganizationModel[]> {
    const response = await this.apiClient.get<IOrganizationModel[]>(
      `/organizations/${uuid}/models`,
    );
    return response.data;
  }

  async getCustomModels(uuid: string): Promise<IModel[]> {
    const response = await this.apiClient.get<IModel[]>(
      `/organizations/${uuid}/custom-models`,
    );
    return response.data;
  }

  // NOTE: NSAI-4640 — org-scoped model pickers for org Chat Settings. The org id
  // is resolved server-side from this UUID (not the active project), so org
  // models stay listed even when the owner is in a personal project. `type`
  // selects the eligibility filter.
  async getProbeEligibleModels(uuid: string): Promise<IModel[]> {
    const response = await this.apiClient.get<IModel[]>(
      `/organizations/${uuid}/eligible-models?type=probe`,
    );
    return response.data;
  }

  async getEmbeddingEligibleModels(
    uuid: string,
  ): Promise<IEmbeddingModelOption[]> {
    const response = await this.apiClient.get<IEmbeddingModelOption[]>(
      `/organizations/${uuid}/eligible-models?type=embedding`,
    );
    return response.data;
  }

  // NOTE: NSAI-4349 — the org model-visibility projection for the Org Settings
  // "Models" tab (owner-only; the server 404-masks non-owners). Returns app rows
  // alongside org-owned rows, each with its per-project visibility matrix (the
  // ORG tier, org_model_visibility). Excludes env-hidden / admin-killed models.
  // Replaced the older GET /org-models (getOrgModels) — which carried no app
  // rows — now that both web and mobile consume this endpoint.
  async getModelsWithVisibility(
    uuid: string,
  ): Promise<IOrganizationOwnedModel[]> {
    const response = await this.apiClient.get<IOrganizationOwnedModel[]>(
      `/organizations/${uuid}/models-with-visibility`,
    );
    return response.data;
  }

  // NOTE: NSAI-4359 — org-OWNED model CRUD for the Org Settings "Models" tab
  // (owner-only; the server 404-masks non-owners). The mutations return a
  // { message } acknowledgement (not the model); org-visibility is a PUT
  // (idempotent upsert of the per-project matrix).
  async createOrgModel({
    uuid,
    data,
  }: ICreateOrgModelParams): Promise<{ message: string }> {
    const response = await this.apiClient.post<{ message: string }>(
      `/organizations/${uuid}/org-models`,
      data,
    );
    return response.data;
  }

  async updateOrgModel({
    uuid,
    modelUuid,
    data,
  }: IUpdateOrgModelParams): Promise<{ message: string }> {
    const response = await this.apiClient.patch<{ message: string }>(
      `/organizations/${uuid}/org-models/${modelUuid}`,
      data,
    );
    return response.data;
  }

  async deleteOrgModel({
    uuid,
    modelUuid,
  }: IDeleteOrgModelParams): Promise<{ message: string }> {
    const response = await this.apiClient.delete<{ message: string }>(
      `/organizations/${uuid}/org-models/${modelUuid}`,
    );
    return response.data;
  }

  // NOTE: NSAI-4349 — the org "Manage Visibility" matrix write. `data.items`
  // sets the ORG tier (org_model_visibility) per project — the Org Owner's
  // authoritative, per-project visibility decision a project member cannot
  // override. PUT because the tab sends the model's complete matrix (idempotent
  // upsert).
  async setOrgModelOrgVisibility({
    uuid,
    modelUuid,
    data,
  }: ISetOrgModelOrgVisibilityParams): Promise<{ message: string }> {
    const response = await this.apiClient.put<{ message: string }>(
      `/organizations/${uuid}/org-models/${modelUuid}/org-visibility`,
      data,
    );
    return response.data;
  }

  // NOTE: NSAI-4349 — the Section-1 org-wide master toggle. isEnabled=false hides
  // the model across every project of the org and gates the per-project matrix.
  // Disabling a model still in use rejects with 409 { message, usage[] }; the
  // caller reads the usage list off the AxiosError to name the areas.
  async setOrgModelOrgWideVisibility({
    uuid,
    modelUuid,
    data,
  }: ISetOrgModelOrgWideVisibilityParams): Promise<{ message: string }> {
    const response = await this.apiClient.patch<{ message: string }>(
      `/organizations/${uuid}/org-models/${modelUuid}/org-wide-visibility`,
      data,
    );
    return response.data;
  }

  // NOTE: NSAI-5880 — the owner's approve / decline of newly released platform
  // models. A collection route taking many uuids, so approving the whole
  // pending list is one call. Rejects with 400 on a deployment whose approval
  // gate is off, and 404s for a uuid that is not an app-owned model.
  async setModelApproval({
    uuid,
    data,
  }: ISetModelApprovalParams): Promise<{ message: string }> {
    const response = await this.apiClient.post<{ message: string }>(
      `/organizations/${uuid}/model-approvals`,
      data,
    );
    return response.data;
  }

  // NOTE: NSAI-4630 — read-only usage view: every DB reference to a model in the
  // org (assistants, automations, org model-settings, probe count). Owner-gated
  // server-side; project-owned / foreign-org models 404.
  async getOrgModelUsage({
    uuid,
    modelUuid,
  }: IGetOrgModelUsageParams): Promise<IOrgModelUsage> {
    const response = await this.apiClient.get<IOrgModelUsage>(
      `/organizations/${uuid}/org-models/${modelUuid}/usage`,
    );
    return response.data;
  }

  async getAssistants(uuid: string): Promise<IOrganizationAssistant[]> {
    const response = await this.apiClient.get<IOrganizationAssistant[]>(
      `/organizations/${uuid}/assistants`,
    );
    return response.data;
  }

  async getRoles(uuid: string): Promise<IOrganizationRole[]> {
    const response = await this.apiClient.get<IOrganizationRole[]>(
      `/organizations/${uuid}/roles`,
    );
    return response.data;
  }

  async createRole({
    uuid,
    data,
  }: ICreateOrganizationRoleParams): Promise<IOrganizationRole> {
    const response = await this.apiClient.post<IOrganizationRole>(
      `/organizations/${uuid}/roles`,
      data,
    );
    return response.data;
  }

  async updateRole({
    uuid,
    roleUuid,
    data,
  }: IUpdateOrganizationRoleParams): Promise<IOrganizationRole> {
    const response = await this.apiClient.patch<IOrganizationRole>(
      `/organizations/${uuid}/roles/${roleUuid}`,
      data,
    );
    return response.data;
  }

  async deleteRole({
    uuid,
    roleUuid,
  }: IDeleteOrganizationRoleParams): Promise<void> {
    await this.apiClient.delete(`/organizations/${uuid}/roles/${roleUuid}`);
  }

  async sendDeleteVerificationCode(uuid: string): Promise<void> {
    await this.apiClient.post(
      `/organizations/${uuid}/send-delete-verification-code`,
    );
  }

  async sendTransferOwnershipVerificationCode(uuid: string): Promise<void> {
    await this.apiClient.post(
      `/organizations/${uuid}/send-transfer-verification-code`,
    );
  }

  async transferOwnership({
    uuid,
    data,
  }: ITransferOrganizationOwnershipParams): Promise<void> {
    await this.apiClient.post(
      `/organizations/${uuid}/transfer-ownership`,
      data,
    );
  }

  async removeOwner({
    uuid,
    data,
  }: IRemoveOrganizationOwnerParams): Promise<void> {
    await this.apiClient.delete(`/organizations/${uuid}/owners`, { data });
  }

  async editIntegrationConfig({
    uuid,
    data,
  }: IUpdateOrganizationIntegrationConfigParams): Promise<void> {
    await this.apiClient.patch(
      `/organizations/${uuid}/integration-configs`,
      data,
    );
  }

  /**
   * NOTE: NSAI-5354 — MCP servers members connected themselves, which the org MCP
   * form could not see. Owner-only; a non-owner gets a 404 from the server.
   *
   * See IOrganizationMcpConnection for why MCP has two stores and why this read
   * exists instead of the two being merged.
   */
  async getMcpConnections(uuid: string): Promise<IOrganizationMcpConnection[]> {
    const response = await this.apiClient.get<IOrganizationMcpConnection[]>(
      `/organizations/${uuid}/mcp-connections`,
    );
    return response.data;
  }

  // NOTE: NSAI-5354 — the owner-managed MCP catalog as approval scopes, for the org
  // connector-approval page's per-server rows.
  async getMcpApprovalTargets(
    uuid: string,
  ): Promise<IOrganizationMcpApprovalTarget[]> {
    const response = await this.apiClient.get<IOrganizationMcpApprovalTarget[]>(
      `/organizations/${uuid}/mcp-approval-targets`,
    );
    return response.data;
  }

  // NOTE: Promotes a member-added MCP server into the owner-managed catalog.
  // Returns the catalog as it now stands, so the caller can seed the MCP form
  // without a second round trip.
  async adoptMcpConnection({
    uuid,
    data,
  }: IAdoptOrganizationMcpConnectionParams): Promise<
    IOrganizationMcpIntegration[]
  > {
    const response = await this.apiClient.post<IOrganizationMcpIntegration[]>(
      `/organizations/${uuid}/mcp-connections/adopt`,
      data,
    );
    return response.data;
  }

  // NOTE: Removes every member connection to one MCP server URL in this
  // organization. Refused for a URL that is in the owner-managed catalog — the
  // next org-form save would simply fan those rows back out, so the honest action
  // there is to delete the catalog entry in the MCP form.
  async deleteMcpConnection({
    uuid,
    data,
  }: IDeleteOrganizationMcpConnectionParams): Promise<void> {
    await this.apiClient.delete(`/organizations/${uuid}/mcp-connections`, {
      data,
    });
  }

  async editSaml({ uuid, data }: IUpdateOrganizationSamlParams): Promise<void> {
    await this.apiClient.patch(`/organizations/${uuid}/saml`, data);
  }

  async getVerifiedDomains(uuid: string): Promise<IVerifiedDomain[]> {
    const response = await this.apiClient.get<IVerifiedDomain[]>(
      `/organizations/${uuid}/verified-domains`,
    );
    return response.data;
  }

  async addVerifiedDomain({
    uuid,
    data,
  }: IAddVerifiedDomainParams): Promise<IVerifiedDomain> {
    const response = await this.apiClient.post<IVerifiedDomain>(
      `/organizations/${uuid}/verified-domains`,
      data,
    );
    return response.data;
  }

  async verifyVerifiedDomain({
    uuid,
    domainUuid,
  }: IVerifyVerifiedDomainParams): Promise<IVerifiedDomain> {
    const response = await this.apiClient.post<IVerifiedDomain>(
      `/organizations/${uuid}/verified-domains/${domainUuid}/verify`,
    );
    return response.data;
  }

  async removeVerifiedDomain({
    uuid,
    domainUuid,
  }: IRemoveVerifiedDomainParams): Promise<void> {
    await this.apiClient.delete(
      `/organizations/${uuid}/verified-domains/${domainUuid}`,
    );
  }

  async getOrganizationInvites(uuid: string): Promise<IOrganizationInvite[]> {
    const response = await this.apiClient.get<IOrganizationInvite[]>(
      `/organizations/${uuid}/invites`,
    );
    return response.data;
  }

  async resendOrganizationInvite({
    uuid,
    inviteUuid,
  }: IResendOrganizationInviteParams): Promise<IOrganizationInvite> {
    const response = await this.apiClient.post<IOrganizationInvite>(
      `/organizations/${uuid}/invites/${inviteUuid}/resend`,
    );
    return response.data;
  }

  async revokeOrganizationInvite({
    uuid,
    inviteUuid,
  }: IRevokeOrganizationInviteParams): Promise<{ message: string }> {
    const response = await this.apiClient.delete<{ message: string }>(
      `/organizations/${uuid}/invites/${inviteUuid}`,
    );
    return response.data;
  }

  async updateChatSettings({
    uuid,
    data,
  }: IUpdateOrganizationChatSettingsParams): Promise<IChatSettings> {
    const response = await this.apiClient.patch<IChatSettings>(
      `/organizations/${uuid}/chat-settings`,
      data,
    );
    return response.data;
  }

  async updateCallAssistantSettings({
    uuid,
    data,
  }: IUpdateCallAssistantSettingsParams): Promise<ICallAssistantSettingsResponse> {
    const response = await this.apiClient.put<ICallAssistantSettingsResponse>(
      `/organizations/${uuid}/call-assistant-settings`,
      data,
    );
    return response.data;
  }

  async getActiveProbeModels(
    organizationUuid: string,
  ): Promise<IProbeModelDefaults> {
    const response = await this.apiClient.get<IProbeModelDefaults>(
      `/organizations/${organizationUuid}/probe-models`,
    );
    return response.data;
  }

  async updateProbeModelSettings({
    uuid,
    data,
  }: IUpdateProbeModelSettingsParams): Promise<void> {
    await this.apiClient.patch(`/organizations/${uuid}/probe-models`, data);
  }

  async updateOrganizationSearchSettings({
    uuid,
    data,
  }: IUpdateOrganizationSearchSettingsParams): Promise<void> {
    await this.apiClient.patch(`/organizations/${uuid}/search-settings`, data);
  }

  async getUsageLimits(
    organizationUuid: string,
  ): Promise<IOrganizationUsageLimits> {
    const response = await this.apiClient.get<IOrganizationUsageLimits>(
      `/organizations/${organizationUuid}/usage-limits`,
    );
    return response.data;
  }

  async updateUsageLimits({
    uuid,
    data,
  }: IUpdateOrganizationUsageLimitsParams): Promise<void> {
    await this.apiClient.patch(`/organizations/${uuid}/usage-limits`, data);
  }

  // NOTE: NSAI-3376 — member-facing read-only overview (owners always allowed,
  // members need the usage_limits:read scope). Separate from getUsageLimits,
  // which is the owner-only config read used by the editing form.
  async getUsageLimitsOverview(
    organizationUuid: string,
  ): Promise<IOrganizationUsageLimitsOverview> {
    const response = await this.apiClient.get<IOrganizationUsageLimitsOverview>(
      `/organizations/${organizationUuid}/usage-limits/overview`,
    );
    return response.data;
  }

  // NOTE: NSAI-5037 — per-model request caps. A separate endpoint pair from the
  // usage limits above: those are period budgets, these bound one request.
  async getModelTokenLimits(
    organizationUuid: string,
  ): Promise<IOrganizationModelTokenLimits> {
    const response = await this.apiClient.get<IOrganizationModelTokenLimits>(
      `/organizations/${organizationUuid}/model-token-limits`,
    );
    return response.data;
  }

  async updateModelTokenLimits({
    uuid,
    data,
  }: IUpdateOrganizationModelTokenLimitsParams): Promise<void> {
    await this.apiClient.patch(
      `/organizations/${uuid}/model-token-limits`,
      data,
    );
  }

  async getDataRetention(
    organizationUuid: string,
  ): Promise<IOrganizationDataRetention> {
    const response = await this.apiClient.get<IOrganizationDataRetention>(
      `/organizations/${organizationUuid}/data-retention`,
    );
    return response.data;
  }

  async updateDataRetention({
    uuid,
    data,
  }: IUpdateOrganizationDataRetentionParams): Promise<void> {
    await this.apiClient.patch(`/organizations/${uuid}/data-retention`, data);
  }

  // NOTE: NSAI-3060 — the organization owner's own SIEM export configuration.
  // Both calls are owner-only server-side; a member gets the same 404 an unknown
  // organization gets, so a rejection here is not a signal to retry.
  async getSiemConfig(
    organizationUuid: string,
  ): Promise<IOrganizationSiemConfig> {
    const response = await this.apiClient.get<IOrganizationSiemConfig>(
      `/organizations/${organizationUuid}/siem-config`,
    );
    return response.data;
  }

  // NOTE: Returns the effective config rather than void (unlike the other
  // organization patches) because the server merges the patch over the stored
  // row — the response is the only place the caller learns which TLS material is
  // now on file, since none of it can be read back.
  async updateSiemConfig({
    uuid,
    data,
  }: IUpdateOrganizationSiemConfigParams): Promise<IOrganizationSiemConfig> {
    const response = await this.apiClient.patch<IOrganizationSiemConfig>(
      `/organizations/${uuid}/siem-config`,
      data,
    );
    return response.data;
  }

  // NOTE: NSAI-6369 — the workspace's memory exclusion list. Gated server-side
  // on the memory_policy family rather than on ownership, so a member holding
  // the permission gets a real answer here where the SIEM read above would 404.
  async getMemoryExclusions(
    organizationUuid: string,
  ): Promise<IOrganizationMemoryExclusionsView> {
    const response =
      await this.apiClient.get<IOrganizationMemoryExclusionsView>(
        `/organizations/${organizationUuid}/memory-exclusions`,
      );
    return response.data;
  }

  // NOTE: PUT with the whole list, like the per-user editor — the payload is
  // what the admin is looking at, and the response is the normalized list that
  // was actually stored.
  async updateMemoryExclusions({
    uuid,
    categories,
  }: IUpdateOrganizationMemoryExclusionsParams): Promise<IOrganizationMemoryExclusionsView> {
    const response =
      await this.apiClient.put<IOrganizationMemoryExclusionsView>(
        `/organizations/${uuid}/memory-exclusions`,
        { categories },
      );
    return response.data;
  }

  async testSiemConfig({
    uuid,
    data,
  }: ITestOrganizationSiemConfigParams): Promise<TSiemConnectionTestResult> {
    const response = await this.apiClient.post<TSiemConnectionTestResult>(
      `/organizations/${uuid}/siem-config/test`,
      data,
    );
    return response.data;
  }

  async getSyncatorApiKeys(
    organizationUuid: string,
  ): Promise<ISyncatorApiKey[]> {
    const response = await this.apiClient.get<ISyncatorApiKey[]>(
      `/organizations/${organizationUuid}/syncator-keys`,
    );
    return response.data;
  }

  async createSyncatorApiKey({
    organizationUuid,
    data,
  }: ICreateSyncatorApiKeyParams): Promise<ISyncatorApiKeyCreateResponse> {
    const response = await this.apiClient.post<ISyncatorApiKeyCreateResponse>(
      `/organizations/${organizationUuid}/syncator-keys`,
      data,
    );
    return response.data;
  }

  async revokeSyncatorApiKey({
    organizationUuid,
    keyUuid,
  }: IRevokeSyncatorApiKeyParams): Promise<void> {
    await this.apiClient.delete(
      `/organizations/${organizationUuid}/syncator-keys/${keyUuid}`,
    );
  }
}
